import { useState } from 'react';
import { useAuth } from '@core/auth/useAuth';
import { Loader2, Sparkles, Wand2 } from 'lucide-react';
import { invokeLlamaChat } from '@core/ai/llamaChat';
import type { ApplicationNewAiContext } from './applicationAssistTypes';

const SYSTEM = `You are a senior recruitment consultant writing internal notes for an ATS.
Be concise, professional, and factual. Do not invent compensation or offers unless the user provided them.
Use short bullets where helpful.`;

function contextBlock(ctx: ApplicationNewAiContext): string {
  return [
    `Candidate: ${ctx.candidateLabel || '(not selected)'}`,
    `Job: ${ctx.jobTitle || '(not selected)'}`,
    `Client: ${ctx.clientName || '(not selected)'}`,
    `Current internal notes (may be empty):\n${ctx.internal_notes || '—'}`,
  ].join('\n');
}

type Props = {
  context: ApplicationNewAiContext;
  onApplyNotes: (text: string, mode: 'append' | 'replace') => void;
};

export function ApplicationNewAiPanel({ context, onApplyNotes }: Props) {
  const { user } = useAuth();
  const [customPrompt, setCustomPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (userPrompt: string) => {
    setError(null);
    setLoading(true);
    setOutput('');
    try {
      const meta =
        user?.orgId != null
          ? { orgId: user.orgId, feature: 'application_ai', orgUserId: user.orgUserId ?? null }
          : null;
      const { content } = await invokeLlamaChat(
        [
          { role: 'system', content: SYSTEM },
          {
            role: 'user',
            content: `${contextBlock(context)}\n\n---\n\nTask:\n${userPrompt}`,
          },
        ],
        undefined,
        meta
      );
      setOutput(content);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const quick = (instruction: string) => () => run(instruction);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-violet-900/50 bg-violet-950/20 px-4 py-3 text-sm text-violet-100/90">
        <p className="flex items-center gap-2 font-medium text-violet-200">
          <Sparkles className="h-4 w-4 shrink-0 text-violet-400" />
          Llama (Groq) — same Edge Function as requisitions
        </p>
        <p className="mt-1 text-xs text-violet-200/70">
          Deploy <code className="rounded bg-black/30 px-1">ggj_b2b_llama</code> and set{' '}
          <code className="rounded bg-black/30 px-1">GROQ_API_KEY</code> in Supabase secrets.
        </p>
      </div>

      <div>
        <p className="text-xs font-medium uppercase tracking-wider vscode-text-tertiary mb-2">[Quick prompts]</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary text-xs py-1.5"
            disabled={loading}
            onClick={quick(
              'Write internal submission notes: fit vs job, top strengths, gaps or risks, and suggested next step for the client. Bullet format.'
            )}
          >
            [Submission rationale]
          </button>
          <button
            type="button"
            className="btn-secondary text-xs py-1.5"
            disabled={loading}
            onClick={quick(
              'List 5 due-diligence questions the recruiter should confirm before client submittal (skills, comp, notice, competing processes).'
            )}
          >
            [Due-diligence checklist]
          </button>
          <button
            type="button"
            className="btn-secondary text-xs py-1.5"
            disabled={loading}
            onClick={quick(
              'Draft a short client-facing email intro (3–4 sentences) we can paste after internal review — professional, no salary unless in notes.'
            )}
          >
            [Client email draft]
          </button>
          <button
            type="button"
            className="btn-secondary text-xs py-1.5"
            disabled={loading}
            onClick={quick(
              'Summarize red flags or sensitivities to watch (only infer from context; if unknown say "not specified").'
            )}
          >
            [Risks / sensitivities]
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs vscode-text-tertiary mb-1">[Custom ask]</label>
        <div className="flex gap-2 flex-col sm:flex-row">
          <textarea
            className="input flex-1 min-h-[72px] text-sm"
            placeholder="e.g. Compare this profile to a typical senior backend hire…"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            disabled={loading}
          />
          <button
            type="button"
            className="btn-primary self-start sm:self-stretch flex items-center justify-center gap-1.5"
            disabled={loading || !customPrompt.trim()}
            onClick={() => run(customPrompt.trim())}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            [Run]
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {loading && (
        <div className="flex items-center gap-2 text-sm vscode-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" /> Generating…
        </div>
      )}

      {output && !loading && (
        <div className="rounded-xl border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_50%,transparent)] p-4">
          <p className="text-xs font-medium vscode-text-tertiary mb-2">[Output]</p>
          <pre className="whitespace-pre-wrap text-sm vscode-text-primary font-sans max-h-[min(420px,50vh)] overflow-y-auto">
            {output}
          </pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn-secondary text-xs py-1.5" onClick={() => onApplyNotes(output.trim(), 'replace')}>
              [Replace internal notes]
            </button>
            <button type="button" className="btn-secondary text-xs py-1.5" onClick={() => onApplyNotes(output.trim(), 'append')}>
              [Append to internal notes]
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
